/* eslint-disable array-element-newline */
/* eslint-disable indent */
/* eslint-disable max-lines-per-function */
import {IMAGE_SIZE, IMAGE_TYPE, PRODUCT_LISTING_ONDEMANDCOLUMNS, ROOM_CAPACITY_UNIT, StateActions, Steps} from 'extensions/multi-system/data/constants';
import {eventStateChange} from 'extensions/multi-system/utils/events';
import {ProductListController} from 'extensions/ui-controllers/product-list/lib/product-list';
import {defineCustomElement, formatPrice} from 'extensions/ui-utils';
import {LitElement, html, nothing} from 'lit';
import {createRef, ref} from 'lit/directives/ref.js';
import './x-summary-product-line';
import stylesheet from './x-summary.scss?as=stylesheet';

export class Summary extends LitElement {
	static get styles () {
		return [stylesheet];
	}

	static get properties () {
		return {
			adpmSubmitted: {
				type: Boolean
			},
			state: {
				state: true,
				type: Object
			},
			summaryProductsMap: {
				state: true,
				type: Array
			},
			summaryTotal: {
				state: true,
				type: Number
			}
		};
	}

	drawerRef = createRef();

	/**
	 * @type {import('extensions/multi-system/types').Room}
	 */
	get selectedRoom () {
		return this.state?.rooms?.[this.state?.selectedRoom];
	}

	get mivaState () {
		return {
			names: [
				...this.state?.rooms?.map(room => [room.product, `${room.name} (${room.selection?.display}${ROOM_CAPACITY_UNIT})`]),
				...this.state?.outdoorProduct
					? [[this.state?.outdoorProduct, 'Outdoor Unit']]
					: [],
				...this.state?.upsellProducts?.map(upsellProduct => [upsellProduct.code, 'Optional Add-On'])
			]
				?.filter(([code]) => code)
				?.reduce((state, [code, name]) => {
					const found = state[code] || [];
					found.push(name);

					return {
						...state,
						[code]: found
					};
				}, {}),
			quantity: 1
		};
	}

	constructor () {
		super();

		/**
		 * @type {boolean}
		 */
		this.adpmSubmitted = false;

		/**
		 * @type {import('extensions/multi-system/types').MultiSystemState}
		 */
		this.state = {};

		/**
		 * @type {Record<string, import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct>}
		 */
		this.summaryProductsMap = {};

		/**
		 * @type {number}
		 */
		this.summaryTotal = 0;
	}

	connectedCallback () {
		super.connectedCallback();

		// Initial update summary data call
		this.updateSummaryData();
	}

	async updated (changedProperties) {
		if (changedProperties.has('state')) {
			await this.updateComplete;

			this.updateSummaryData();
		}
	}

	async getSummaryProductsMap () {
		// Generate array of product codes
		const productCodes = [
			...this.state?.rooms?.map(room => room.product) || [],
			...this.state?.upsellProducts?.map(upsell => upsell.code) || [],
			...this.state?.requiredProducts?.map(requiredProduct => requiredProduct.code) || [],
			...this.state?.outdoorProduct
				? [this.state?.outdoorProduct]
				: []
		];

		// Skip running query if no productCodes are selected
		if (productCodes.length === 0) {
			return {};
		}

		const productListController = new ProductListController(this, {
			cache: true,
			count: 0,
			filters: [
				{
					name: 'search',
					value: productCodes.map(code => ({
						field: 'code',
						operator: 'EQ',
						value: code
					}))
				},
				{
					name: 'imagetypes',
					value: {
						sizes: [
							IMAGE_SIZE,
							'original'
						],
						types: [IMAGE_TYPE]
					}
				}
			],
			onDemandColumns: [
				...PRODUCT_LISTING_ONDEMANDCOLUMNS,
				'category'
			]
		});

		const products = await productListController.getProducts();

		return products?.reduce((map, product) => {
			return {
				...map,
				[product.code]: product
			};
		}, {});
	}

	calculateSummaryTotal () {
		return [
			...(this.state?.rooms || [])
				?.map(room => this.summaryProductsMap[room.product]?.price),
			...this.state?.outdoorProduct
				? [this.summaryProductsMap[this.state?.outdoorProduct]?.price]
				: [],
			...(this.state?.requiredProducts || [])
				?.map(requiredProduct => this.summaryProductsMap[requiredProduct.code]?.price * requiredProduct?.quantity),
			...(this.state?.upsellProducts || [])
				?.map(upsellProduct => this.summaryProductsMap[upsellProduct.code]?.price * upsellProduct?.quantity)
		]
			?.filter(price => price)
			?.reduce((total, price) => total + price, 0);
	}

	async updateSummaryData () {
		this.summaryProductsMap = await this.getSummaryProductsMap();
		this.summaryTotal = await this.calculateSummaryTotal();

		this.dispatchEvent(new CustomEvent('productchange', {
			bubbles: true,
			detail: this.summaryProductsMap
		}));
	}

	handleEditRoomsClick () {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: [
				{
					action: StateActions.StepUpdate,
					merge: true,
					payload: Steps.RoomCreation
				},
				{
					action: StateActions.SelectedRoomUpdate,
					merge: true,
					payload: 0
				}
			]
		}));
	}

	/**
	 * @param {number} roomNumber
	 */
	handleRoomOrStepChange (roomNumber) {
		// Change step back to RoomCreation if roomNumber is less than selected room index
		if (this.state?.selectedRoom === 0 && roomNumber < this.state?.selectedRoom) {
			this.dispatchEvent(eventStateChange({
				bubbles: true,
				composed: true,
				detail: {
					action: StateActions.StepUpdate,
					merge: true,
					payload: Steps.RoomCreation
				}
			}));
		}
		else if (roomNumber >= this.state?.roomCount) {
			const direction = roomNumber > this.state?.step ? 'next' : 'prev';
			let nextStep = Steps.Outdoor;
			if (this.state?.step === Steps.Outdoor && direction === 'next') {
				nextStep = Steps.RequiredProducts;
			} else if (this.state?.step === Steps.RequiredProducts && direction === 'next') {
				nextStep = Steps.Upsell;
			} else if (this.state?.step === Steps.Upsell && typeof this.state?.requiredProductsCountInit !== 'undefined' && this.state?.requiredProductsCount > 0 && direction === 'prev') {
				nextStep = Steps.RequiredProducts;
			}

			this.dispatchEvent(eventStateChange({
				bubbles: true,
				composed: true,
				detail: [
					{
						action: StateActions.StepUpdate,
						merge: true,
						payload: nextStep
					},
					{
						action: StateActions.SelectedRoomUpdate,
						merge: true,
						payload: roomNumber
					}
				]
			}));
		}
		else {
			this.dispatchEvent(eventStateChange({
				bubbles: true,
				composed: true,
				detail: [
					{
						action: StateActions.StepUpdate,
						merge: true,
						payload: Steps.Indoor
					},
					{
						action: StateActions.SelectedRoomUpdate,
						merge: true,
						payload: roomNumber
					}
				]
			}));
		}
	}

	async handleStepReview () {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: {
				action: StateActions.StepUpdate,
				merge: true,
				payload: Steps.Review
			}
		}));

		// Delay until after state propagates back down the tree
		await this.updateComplete;
		setTimeout(() => this.drawerRef?.value?.openDrawer(), 0);
	}

	handleRequiredProductQuantityChange (e, productCode) {
		const found = this.state?.requiredProducts?.map(requiredProduct => requiredProduct.code === productCode);

		if (found && found.quantity !== e.detail.value) {
			this.dispatchEvent(eventStateChange({
				bubbles: true,
				composed: true,
				detail: {
					action: StateActions.RequiredProductQuantityUpdate,
					merge: true,
					payload: {
						code: productCode,
						quantity: e.detail.value
					}
				}
			}));
		}
	}

	handleRequiredProductDelete (e, productCode) {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: {
				action: StateActions.RequiredProductRemove,
				merge: true,
				payload: productCode
			}
		}));
	}

	handleUpsellProductQuantityChange (e, productCode) {
		const found = this.state?.upsellProducts?.map(upsellProduct => upsellProduct.code === productCode);

		if (found && found.quantity !== e.detail.value) {
			this.dispatchEvent(eventStateChange({
				bubbles: true,
				composed: true,
				detail: {
					action: StateActions.UpsellProductQuantityUpdate,
					merge: true,
					payload: {
						code: productCode,
						quantity: e.detail.value
					}
				}
			}));
		}
	}

	handleUpsellProductDelete (e, productCode) {
		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: {
				action: StateActions.UpsellProductRemove,
				merge: true,
				payload: productCode
			}
		}));
	}

	handleIndoorProductClick (index) {
		this.drawerRef?.value?.closeDrawer();

		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: [
				{
					action: StateActions.SelectedRoomUpdate,
					merge: true,
					payload: index
				},
				{
					action: StateActions.StepUpdate,
					merge: true,
					payload: Steps.Indoor
				}
			]
		}));
	}

	handleOutdoorProductClick () {
		this.drawerRef?.value?.closeDrawer();

		this.dispatchEvent(eventStateChange({
			bubbles: true,
			composed: true,
			detail: [
				{
					action: StateActions.SelectedRoomUpdate,
					merge: true,
					payload: this.state?.roomCount + 1
				},
				{
					action: StateActions.StepUpdate,
					merge: true,
					payload: Steps.Outdoor
				}
			]
		}));
	}

	handleDrawerChange () {
		this.requestUpdate();
	}

	renderRequiredProducts () {
		if (!this.state?.requiredProducts?.length) {
			return nothing;
		}

		return this.state?.requiredProducts?.map(requiredProduct => {

			/**
			 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct}
			 */
			const product = this.summaryProductsMap[requiredProduct.code];

			// Multiply price by quantity and reformat
			const price = formatPrice(product?.price * requiredProduct.quantity);

			return html`
				<x-summary-product-line
					@change="${(e) => this.handleRequiredProductQuantityChange(e, product?.code)}"
					@delete="${(e) => this.handleRequiredProductDelete(e, product?.code)}"
					?empty="${!product}"
					label="Required Product"
					price="${price}"
					show-quantity
					quantity="${requiredProduct.quantity}"
					category="${product?.category?.name}"
					image-url="${product?.imagetypes[IMAGE_TYPE]?.sizes[IMAGE_SIZE]?.url || product?.imagetypes[IMAGE_TYPE]?.sizes?.original?.url}"
					sku="${product?.sku}"
				></x-summary-product-line>
			`;
		});
	}

	renderUpsellProducts () {
		if (!this.state?.upsellProducts?.length) {
			return html`<x-summary-product-line
				empty
				label="Optional Add-Ons"
			></x-summary-product-line>`;
		}

		return this.state?.upsellProducts?.map(upsellProduct => {

			/**
			 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct}
			 */
			const product = this.summaryProductsMap[upsellProduct.code];

			// Multiply price by quantity and reformat
			const price = formatPrice(product?.price * upsellProduct.quantity);

			return html`
				<x-summary-product-line
					@change="${(e) => this.handleUpsellProductQuantityChange(e, product?.code)}"
					@delete="${(e) => this.handleUpsellProductDelete(e, product?.code)}"
					?empty="${!product}"
					label="Optional Add-Ons"
					price="${price}"
					show-quantity
					quantity="${upsellProduct.quantity}"
					category="${product?.category?.name}"
					image-url="${product?.imagetypes[IMAGE_TYPE]?.sizes[IMAGE_SIZE]?.url || product?.imagetypes[IMAGE_TYPE]?.sizes?.original?.url}"
					sku="${product?.sku}"
					gas-levy="${product?.CustomField_Values?.customfields?.GasLevyAmount}"
					refrigerant-type="${product?.CustomField_Values?.customfields?.RefrigerantType}"
				></x-summary-product-line>
		`;
		});
	}

	// eslint-disable-next-line array-element-newline
	renderOutdoorProduct () {

		/**
		 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct}
		 */
		const product = this.summaryProductsMap[this.state?.outdoorProduct];

		return html`
			<button class="empty-button room-product-line" @click="${this.handleOutdoorProductClick}">
				<x-summary-product-line
					cooling-capacity="${product?.CustomField_Values?.customfields?.CapacityCoolDisplaykW}"
					?empty="${!product}"
					heating-capacity="${product?.CustomField_Values?.customfields?.CapacityHeatDisplaykW}"
					label="Outdoor Unit"
					price="${product?.formatted_price}"
					category="${product?.category?.name || 'Outdoor Unit'}"
					image-url="${product?.imagetypes[IMAGE_TYPE]?.sizes[IMAGE_SIZE]?.url || product?.imagetypes[IMAGE_TYPE]?.sizes?.original?.url}"
					sku="${product?.sku}"
					gas-levy="${product?.CustomField_Values?.customfields?.GasLevyAmount}"
					refrigerant-type="${product?.CustomField_Values?.customfields?.RefrigerantType}"
				></x-summary-product-line>
			</button>
		`;
	}

	renderIndoorProducts () {
		return this.state?.rooms?.map((room, index) => {

			/**
			 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct}
			 */
			const product = this.summaryProductsMap[room.product];

			return html`
				<button class="empty-button room-product-line" @click="${() => this.handleIndoorProductClick(index)}">
					<x-summary-product-line
						cooling-capacity="${product?.CustomField_Values?.customfields?.CapacityCoolDisplaykW}"
						?empty="${!product}"
						heating-capacity="${product?.CustomField_Values?.customfields?.CapacityHeatDisplaykW}"
						label="${room.name} (${room.selection?.display}${ROOM_CAPACITY_UNIT})"
						price="${product?.formatted_price}"
						category="${product?.category?.name}"
						image-url="${product?.imagetypes[IMAGE_TYPE]?.sizes[IMAGE_SIZE]?.url || product?.imagetypes[IMAGE_TYPE]?.sizes?.original?.url}"
						sku="${product?.sku}"
						gas-levy="${product?.CustomField_Values?.customfields?.GasLevyAmount}"
						refrigerant-type="${product?.CustomField_Values?.customfields?.RefrigerantType}"
					></x-summary-product-line>
				</button>
			`;
		});
	}

	renderDrawerContent () {
		return html`
			<div class="drawer-sub-heading">
				<button class="edit-button empty-button" @click="${this.handleEditRoomsClick}">
					<span>${this.state?.roomCount} Rooms</span>
					<span class="edit-icon"></span>
				</button>
			</div>
			<div class="grid-table">
				<header class="grid-table__header-row bold">
					<div class="grid-table__column">Room</div>
					<div class="grid-table__column">Product</div>
					<div class="grid-table__column">Capacity</div>
					<div class="grid-table__column center">Qty</div>
					<div class="grid-table__column right">Price</div>
				</header>
				<main class="grid-table__content">
					${this.renderIndoorProducts()}
					${this.renderOutdoorProduct()}
					${this.renderRequiredProducts()}
					${this.renderUpsellProducts()}
				</main>
			</div>
		`;
	}

	/**
	 * @param {string} productCode
	 * @param {number} count
	 */
	renderProductGasLevyAttributes (productCode, count) {

		/**
		 * @type {import('extensions/ui-controllers/product-list/lib/product-list').MivaProduct}
		 */
		const product = this.summaryProductsMap[productCode];

		if (product.CustomField_Values?.customfields?.GasLevyAmount && product.CustomField_Values?.customfields?.RefrigerantType) {
			return html`
				<input type="hidden" name="Products[${count}]:attributes[1]:code" value="GasLevy" />
				<input type="hidden" name="Products[${count}]:attributes[1]:value" value="${product.CustomField_Values?.customfields?.RefrigerantType}" />
			`;
		}

		return nothing;
	}

	renderDrawerButtons () {
		if (this.drawerRef?.value?.open) {
			if (this.state?.step === Steps.Upsell || this.state?.step === Steps.Review) {
				let count = 0;

				const renderOutdoorProductInputs = () => {
					count++;

					return html`
						<input type="hidden" name="Products[${count}]:code" value="${this.state?.outdoorProduct}" />
						<input type="hidden" name="Products[${count}]:quantity" value="1" />
						${this.renderProductGasLevyAttributes(this.state?.outdoorProduct, count)}
					`;
				};

				return html`
					<form action="${window.mivaJS.Basket_Url}" method="post" @submit="${this.handleADPMSubmit}">
						<input type="hidden" name="Action" value="ADPM" />
						<input type="hidden" name="Attributes" value="Yes">
						<input type="hidden" name="Store_Code" value="${window.mivaJS.Store_Code}" />
						<input type="hidden" name="MultiSystemGroup" value="${this.state?.id}" />
						<input type="hidden" name="MultiSystemState" value="${JSON.stringify(this.mivaState)}" />

						${this.state?.rooms?.map(room => {
							count++;

							return html`
								<input type="hidden" name="Products[${count}]:code" value="${room.product}" />
								<input type="hidden" name="Products[${count}]:quantity" value="1" />
								${this.renderProductGasLevyAttributes(room.product, count)}
							`;
						})}

						${renderOutdoorProductInputs()}

						${this.state?.requiredProducts?.map(requiredProduct => {
							count++;

							return html`
								<input type="hidden" name="Products[${count}]:code" value="${requiredProduct.code}" />
								<input type="hidden" name="Products[${count}]:quantity" value="${requiredProduct.quantity}" />
							`;
						})}

						${this.state?.upsellProducts?.map(upsellProduct => {
							count++;

							return html`
								<input type="hidden" name="Products[${count}]:code" value="${upsellProduct.code}" />
								<input type="hidden" name="Products[${count}]:quantity" value="${upsellProduct.quantity}" />
								${this.renderProductGasLevyAttributes(upsellProduct.code, count)}
							`;
						})}

						<button class="continue-button" type="submit" ?disabled="${this.adpmSubmitted}">Add to Cart</button>
					</form>
				`;
			}

			return html`
				<button class="continue-button" @click="${() => this.drawerRef?.value?.closeDrawer()}">Continue Build</button>
			`;
		}

		switch (this.state?.step) {
			default:
			case Steps.Indoor:
			case Steps.Outdoor: {
				const disabled = this.state?.step === Steps.Indoor
					? !this.selectedRoom?.product
					: !this.state?.outdoorProduct;

				return html`
					<button class="previous-room-button empty-button" @click="${() => this.handleRoomOrStepChange(this.state?.selectedRoom - 1)}">Previous Step</button>

					<x-tooltip ?disabled="${!disabled}">
						<div slot="content">Select a product to continue</div>
						<button class="next-room-button has-icon" ?disabled="${disabled}" @click="${() => this.handleRoomOrStepChange(this.state?.selectedRoom + 1)}">Next<span class="next-room-icon" aria-hidden="true"></span></button>
					</x-tooltip>
				`;
			}
			case Steps.Review:
			case Steps.Upsell:
				return html`
					<button class="previous-room-button empty-button" @click="${() => this.handleRoomOrStepChange(this.state?.selectedRoom - 1)}">Previous Step</button>

					<button class="upsell-button" @click="${this.handleStepReview}">
						${this.state?.upsellProducts?.length ? 'Review Selections' : 'Skip Add-Ons'}
					</button>
				`;
		}
	}

	renderTotal () {
		if (!window.mivaJS?.Customer_Permissions?.WebstorePricing) {
			return html`<span class="price price-hyphen"></span>`;
		}

		return html`TOTAL: ${formatPrice(this.summaryTotal)}`;
	}

	render () {
		if (this.state?.step !== Steps.RoomCreation) {
			return html`
				<x-drawer class="drawer" @change="${this.handleDrawerChange}" ${ref(this.drawerRef)}>
					<div slot="heading">Multi System Summary</div>

					<div class="drawer-title" slot="trigger-title">${this.renderTotal()}</div>

					<div class="drawer-button-group" slot="trigger">
						${this.renderDrawerButtons()}
					</div>

					<div class="drawer-content">
						${this.renderDrawerContent()}
					</div>
				</x-drawer>
			`;
		}

		return nothing;
	}

	handleADPMSubmit () {
		this.adpmSubmitted = true;

		const products = [];
		for (const product of Object.keys(this.summaryProductsMap)) {
			products.push({
				/* eslint-disable camelcase */
				item_id: this.summaryProductsMap[product]?.code,
				item_list_id: 'multisystem',
				item_list_name: 'Multisystem',
				item_name: this.summaryProductsMap[product]?.name,
				/* eslint-enable camelcase */
				price: this.summaryProductsMap[product]?.price,
				quantity: this.summaryProductsMap[product]?.quantity || 1
			});
		}

		if (products.length) {
			window.dataLayer.push({
				ecommerce: null
			});

			window.dataLayer.push({
				ecommerce: {
					add: {
						products: products
					},
					currencyCode: window.mivaJS?.Currency_Code
				},
				event: 'addToCart'
			});
		}
	}
};

defineCustomElement('x-summary', Summary);
